import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Inbox, Send, ArrowLeft, RefreshCw, Search, Mail, PenSquare } from "lucide-react";

function FromSelect({ status, value, onChange, testid }) {
  const opts = status?.from_options?.length ? status.from_options : (status?.default_from ? [{ email: status.default_from, default: true }] : []);
  return (
    <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)} className="border border-[#E5E7EB] bg-white px-2 py-1 text-sm">
      {opts.map(s => <option key={s.email} value={s.email}>{s.email}{s.default ? " (default)" : ""}{s.verified === false ? " · unverified" : ""}</option>)}
    </select>
  );
}

export function GmailInbox() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [fromAddr, setFromAddr] = useState("");
  const [sending, setSending] = useState(false);
  const [compose, setCompose] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [assignRef, setAssignRef] = useState("");
  const [assigning, setAssigning] = useState(false);

  const loadStatus = () => api.get("/admin/gmail/status").then(r => {
    setStatus(r.data);
    if (r.data.connected) setFromAddr(r.data.default_from || "");
    return r.data;
  });

  const loadMessages = (query = "") => {
    setLoading(true);
    api.get(`/admin/gmail/messages?q=${encodeURIComponent(query)}`)
      .then(r => setItems(r.data.items || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Could not load inbox"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStatus().then(s => { if (s.connected) loadMessages(); else setLoading(false); });
    api.get("/admin/bookings").then(r => setBookings(r.data.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: initial inbox/status fetch; intentionally runs once
  }, []);

  const assignToJob = async () => {
    if (!assignRef || assigning) return;
    setAssigning(true);
    try {
      await api.post("/admin/gmail/assign", { message_id: open.id, reference: assignRef });
      const b = bookings.find(x => x.reference === assignRef);
      toast.success(`Email assigned to ${b ? b.name + " · " : ""}${assignRef}`);
      setAssignRef("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign email"); }
    setAssigning(false);
  };

  const openMsg = async (id) => {
    setOpenLoading(true);
    setOpen({ id });
    setReply("");
    setFromAddr(status?.default_from || fromAddr || "");
    try {
      const r = await api.get(`/admin/gmail/message/${id}`);
      setOpen(r.data);
      setItems(prev => prev.map(m => m.id === id ? { ...m, unread: false } : m));
    } catch (e) { toast.error("Could not open email"); setOpen(null); }
    setOpenLoading(false);
  };

  const send = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      const subject = (open.subject || "").toLowerCase().startsWith("re:") ? open.subject : `Re: ${open.subject || ""}`;
      await api.post("/admin/gmail/reply", {
        to: open.from_email, subject, body: reply,
        thread_id: open.thread_id, in_reply_to: open.message_id_header,
        references: open.references, from_addr: fromAddr || undefined,
      });
      toast.success(`Reply sent to ${open.from_email} from ${fromAddr}`);
      setReply("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reply"); }
    setSending(false);
  };

  const sendCompose = async () => {
    if (!compose.to.trim() || !compose.subject.trim() || !compose.body.trim() || sending) {
      if (!sending) toast.error("To, subject and message are required"); return;
    }
    setSending(true);
    try {
      await api.post("/admin/gmail/reply", { to: compose.to.trim(), subject: compose.subject.trim(), body: compose.body, from_addr: fromAddr || undefined });
      toast.success(`Email sent to ${compose.to.trim()} from ${fromAddr}`);
      setCompose(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
    setSending(false);
  };

  if (loading && !status) return <div className="text-[#6B7280] text-sm">Loading…</div>;

  if (status && !status.connected) {
    return (
      <div className="border border-dashed border-[#E5E7EB] bg-white p-10 text-center" data-testid="gmail-not-connected">
        <Mail size={28} className="mx-auto text-[#C79A3B] mb-3" />
        <p className="text-sm font-bold">Gmail isn't connected yet</p>
        <p className="text-xs text-[#6B7280] mt-1 max-w-md mx-auto">Go to <b>Settings → Connected email</b> and click <b>Connect Gmail</b> to read, reply and compose customer emails here.</p>
      </div>
    );
  }

  // Compose new email
  if (compose) {
    return (
      <div data-testid="gmail-compose">
        <button data-testid="gmail-compose-back" onClick={() => setCompose(null)} className="de-btn-secondary mb-4"><ArrowLeft size={16} /> Inbox</button>
        <div className="border border-[#E5E7EB] bg-white">
          <div className="border-b border-[#E5E7EB] p-3 flex items-center gap-2">
            <span className="label-eyebrow">From</span>
            <FromSelect status={status} value={fromAddr} onChange={setFromAddr} testid="gmail-compose-from" />
          </div>
          <input data-testid="gmail-compose-to" value={compose.to} onChange={e => setCompose({ ...compose, to: e.target.value })} placeholder="To (email address)" className="w-full border-b border-[#E5E7EB] px-3 py-2 text-sm" />
          <input data-testid="gmail-compose-subject" value={compose.subject} onChange={e => setCompose({ ...compose, subject: e.target.value })} placeholder="Subject" className="w-full border-b border-[#E5E7EB] px-3 py-2 text-sm" />
          <textarea data-testid="gmail-compose-body" rows={8} value={compose.body} onChange={e => setCompose({ ...compose, body: e.target.value })} placeholder="Write your message…" className="w-full px-3 py-2 text-sm" />
          <div className="border-t border-[#E5E7EB] p-3">
            <button data-testid="gmail-compose-send" disabled={sending} onClick={sendCompose} className="de-btn-primary inline-flex items-center gap-2 disabled:opacity-60"><Send size={16} /> {sending ? "Sending…" : "Send email"}</button>
          </div>
        </div>
      </div>
    );
  }

  // Reading a single email
  if (open) {
    return (
      <div data-testid="gmail-message-view">
        <button data-testid="gmail-back" onClick={() => setOpen(null)} className="de-btn-secondary mb-4"><ArrowLeft size={16} /> Inbox</button>
        {openLoading ? <div className="text-[#6B7280] text-sm">Loading email…</div> : (
          <div className="border border-[#E5E7EB] bg-white">
            <div className="border-b border-[#E5E7EB] p-4">
              <div className="font-bold text-lg">{open.subject}</div>
              <div className="text-sm text-[#6B7280] mt-1">From <b className="text-[#12263A]">{open.from}</b></div>
              <div className="text-xs text-[#6B7280]">{open.date}</div>
            </div>
            <div className="border-b border-[#E5E7EB] p-3 bg-[#F4F4F4] flex items-center gap-2 flex-wrap" data-testid="gmail-assign-bar">
              <span className="label-eyebrow">Assign to job file</span>
              <select data-testid="gmail-assign-select" value={assignRef} onChange={e => setAssignRef(e.target.value)} className="border border-[#E5E7EB] bg-white px-2 py-1 text-sm min-w-[220px]">
                <option value="">Select a client…</option>
                {bookings.map(b => <option key={b.reference} value={b.reference}>{b.name} · {b.reference}</option>)}
              </select>
              <button data-testid="gmail-assign-btn" onClick={assignToJob} disabled={!assignRef || assigning} className="de-btn-secondary text-xs disabled:opacity-50">{assigning ? "Assigning…" : "Assign"}</button>
            </div>
            <div className="p-4 max-h-[40vh] overflow-y-auto" data-testid="gmail-message-body">
              {open.body_text
                ? <pre className="text-sm whitespace-pre-wrap font-sans">{open.body_text}</pre>
                : open.body_html
                  ? <iframe title="email" sandbox="" srcDoc={open.body_html} className="w-full h-72 border border-[#E5E7EB]" />
                  : <p className="text-sm text-[#6B7280]">{open.snippet || "(no body)"}</p>}
            </div>
            <div className="border-t border-[#E5E7EB] p-4 bg-[#F4F4F4]">
              <div className="flex items-center gap-2 mb-2">
                <span className="label-eyebrow">Reply from</span>
                <FromSelect status={status} value={fromAddr} onChange={setFromAddr} testid="gmail-reply-from" />
              </div>
              <textarea data-testid="gmail-reply-body" rows={4} value={reply} onChange={e => setReply(e.target.value)}
                placeholder={`Reply to ${open.from_email}…`} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
              <div className="mt-2">
                <button data-testid="gmail-reply-send" disabled={sending || !reply.trim()} onClick={send} className="de-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                  <Send size={16} /> {sending ? "Sending…" : "Send reply"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Inbox list
  return (
    <div data-testid="gmail-inbox">
      <div className="flex items-center gap-2 mb-4">
        <button data-testid="gmail-compose-btn" onClick={() => { setCompose({ to: "", subject: "", body: "" }); setFromAddr(status?.default_from || ""); }}
          className="de-btn-primary text-xs inline-flex items-center gap-1.5"><PenSquare size={14} /> Compose</button>
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input data-testid="gmail-search" value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") loadMessages(q); }}
            placeholder="Search Gmail (e.g. from:john subject:quote)…"
            className="w-full border border-[#E5E7EB] bg-white pl-9 pr-3 py-2 text-sm" />
        </div>
        <button data-testid="gmail-refresh" onClick={() => loadMessages(q)} className="de-btn-secondary text-xs inline-flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
      </div>
      {loading ? <div className="text-[#6B7280] text-sm">Loading inbox…</div> : (
        <div className="space-y-2">
          {items.map(m => (
            <button key={m.id} data-testid={`gmail-row-${m.id}`} onClick={() => openMsg(m.id)}
              className={`w-full text-left border p-4 flex items-start gap-3 hover:bg-[#FBF3EC] ${m.unread ? "border-[#C79A3B] bg-[#FCF1EA]" : "border-[#E5E7EB] bg-white"}`}>
              <Inbox size={16} className={`mt-0.5 shrink-0 ${m.unread ? "text-[#C79A3B]" : "text-[#6B7280]"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-3">
                  <div className={`text-sm truncate ${m.unread ? "font-black" : "font-semibold"}`}>{m.from_name || m.from_email}</div>
                  <div className="text-xs text-[#6B7280] whitespace-nowrap">{m.date ? new Date(m.date).toLocaleDateString("en-GB") : ""}</div>
                </div>
                <div className={`text-sm truncate ${m.unread ? "font-semibold" : ""}`}>{m.subject}</div>
                <div className="text-xs text-[#6B7280] truncate">{m.snippet}</div>
              </div>
            </button>
          ))}
          {items.length === 0 && (
            <div className="border border-dashed border-[#E5E7EB] p-10 text-center text-[#6B7280]" data-testid="gmail-empty">
              <Inbox size={24} className="mx-auto mb-3 opacity-50" /> No emails found.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
